import { Link, graphql } from "gatsby"
import { formatPostDate } from "utils/helpers"
import kebabCase from "lodash.kebabcase"
import get from "lodash.get"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import useTranslations from "hooks/useTranslations"
import { localizedSlug } from "utils/gatsby-node-helpers"
import { TagList, Tag } from "components/atoms/Tags"
import ContentPageContainer from "components/molecules/common/ContentPageContainer"
import SEO from "../components/seo"
import * as styles from "./blogIndexTemplate.module.css"

const BlogIndexTemplate = ({ pageContext, data }) => {
  const { tag: currentTag, tagsForLocale, isDefault } = pageContext
  const posts = get(data, "allMdx.edges")
  const filteredPosts = posts.filter((post) =>
    currentTag
      ? post.node.frontmatter.tags &&
        post.node.frontmatter.tags.includes(currentTag)
      : true,
  )

  const { blogTitle, blogNoPosts, blogAllPosts } = useTranslations()
  return (
    <>
      <SEO title={blogTitle} />
      <ContentPageContainer title={blogTitle}>
        <TagList>
          <Tag tag={currentTag} to="/blog" current={currentTag === null}>
            {blogAllPosts}
          </Tag>
          {tagsForLocale.map((tag) => (
            <Tag
              tag={tag}
              to={`/blog/tags/${kebabCase(tag)}`}
              current={currentTag === tag}
              key={tag}
            >
              {tag}
            </Tag>
          ))}
        </TagList>
        <main className="box-content space-y-12 py-8 text-neutral-800">
          {!filteredPosts.length && <p>{blogNoPosts}</p>}
          {filteredPosts.map(({ node }) => {
            const title = get(node, "frontmatter.title")
            const externalUrl = get(node, "frontmatter.external_url")
            return (
              <article
                key={node.frontmatter.slug}
                className={styles.withSidebar}
              >
                <div>
                  <GatsbyImage
                    image={getImage(node.frontmatter.image)}
                    className={styles.postImageWrapper}
                    imgStyle={{
                      objectFit: "contain",
                      objectPosition: "top center",
                      borderRadius: "0.5rem",
                      height: "auto",
                    }}
                  />
                  <section>
                    <header>
                      <h3 className={styles.postLink}>
                        {externalUrl ? (
                          <a
                            style={{ boxShadow: "none" }}
                            href={externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {title}
                            <span
                              aria-hidden="true"
                              className="ml-2 inline-block align-middle text-xs font-bold uppercase tracking-wider text-teal-600"
                            >
                              ↗ LinkedIn
                            </span>
                          </a>
                        ) : (
                          <Link
                            style={{ boxShadow: "none" }}
                            to={localizedSlug({
                              isDefault,
                              locale: node.fields.locale,
                              slug: `${node.frontmatter.type}/${node.frontmatter.slug}`,
                            })}
                            rel="bookmark"
                          >
                            {title}
                          </Link>
                        )}
                      </h3>
                      <small>{formatPostDate(node.frontmatter.date)}</small>
                    </header>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: node.frontmatter.spoiler,
                      }}
                    />
                  </section>
                </div>
              </article>
            )
          })}
        </main>
      </ContentPageContainer>
    </>
  )
}

export default BlogIndexTemplate

export const pageQuery = graphql`
  query ($locale: String!) {
    allMdx(
      sort: { frontmatter: { date: DESC } }
      filter: {
        frontmatter: { hidden: { ne: true }, type: { eq: "blog" } }
        fields: { locale: { eq: $locale } }
      }
    ) {
      edges {
        node {
          fields {
            locale
            isDefault
          }
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            spoiler
            slug
            type
            tags
            external_url
            image {
              childImageSharp {
                gatsbyImageData(width: 300, layout: CONSTRAINED)
              }
            }
          }
        }
      }
    }
  }
`
